import React, { useState, useEffect } from 'react';
import { createGuestDonation, queryGuestForDonation } from "../../service/DonationApi";
import { queryCategoryConfig } from "../../service/UtilApi";
import { Helmet } from "react-helmet-async";
import { Navbar, Footer, Loading, Breadcrumbs } from "../../components/shared";
import { useCheckAccess, formatINR, getCurrentDateForTransactionId } from "../../utils/Utilities";
import { useNavigate, useSearchParams } from "react-router-dom";
import '../general/General.css';

const getTodayDate = () => new Date().toISOString().split('T')[0];

const generateTransactionId = ({ mobile, category, subcategory, uniqueOption, amount, date }) => {
    if (!category || !subcategory) throw new Error("Missing input");
    const base = `${mobile + '_guest'}_${category}_${subcategory}`.replace(/\s+/g, '');
    if (uniqueOption === 'familyId') return base;
    const currentDate = getCurrentDateForTransactionId(date);
    return `${base}_${currentDate}_${amount}`;
};

const GuestDonationCreate = () => {
    useCheckAccess('DonationRW');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const mobileParam = searchParams.get("mobile");

    const [mobile, setMobile] = useState('');
    const [searchResult, setSearchResult] = useState(null);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [validationError, setValidationError] = useState('');
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [categoriesError, setCategoriesError] = useState('');
    const [categoryName, setCategoryName] = useState('');
    const [selectedSubcategories, setSelectedSubcategories] = useState([]);
    const [donationDate, setDonationDate] = useState(getTodayDate());
    const [transactionMode, setTransactionMode] = useState('cash');
    const [externalTransactionReference, setExternalTransactionReference] = useState('');
    const [loading, setLoading] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [pendingDonationData, setPendingDonationData] = useState(null);
    const [selfSponsoredWarning, setSelfSponsoredWarning] = useState(false);

    const [guestForm, setGuestForm] = useState({
        name: '', mobile: '', area: '', village: '', taluk: '', district: '', pin: ''
    });
    useEffect(() => {
        if (mobileParam) {
            setMobile(mobileParam);
        }
    }, [mobileParam]);
    // Trigger search if param is present
    useEffect(() => {
        if (mobileParam && mobile) {
            handleSearchGuest(); // auto-click search
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mobile]);
    // Fetch categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const resp = await queryCategoryConfig();
                if (resp?.categoriesList?.length) setCategories(resp.categoriesList);
                else setCategoriesError("No categories found");
            } catch {
                setCategoriesError("Failed to load categories");
            } finally { setCategoriesLoading(false); }
        };
        fetchCategories();
    }, []);

    // Populate guestForm if guest found
    useEffect(() => {
        if (searchResult?.members?.[0]) {
            const g = searchResult.members[0];
            setGuestForm({
                name: g.name || '',
                mobile: g.mobile || '',
                area: g.area || '',
                village: g.village || '',
                taluk: g.taluk || '',
                district: g.district || '',
                pin: g.pin || ''
            });
        }
    }, [searchResult]);
    const fieldTranslations = {
        name: "பெயர்",
        mobile: "மொபைல்",
        area: "ஊர்",
        village: "அஞ்சல்",
        taluk: "தாலுகா",
        district: "மாவட்டம்",
        pin: "பின்கோடு",
    };

    const handleGuestInputChange = (field, value) => setGuestForm(prev => ({ ...prev, [field]: value }));

    const selectedCategory = categories.find(c => c.categoryName === categoryName);
    const sortedSubcategories = selectedCategory?.subcategories
        ? [...selectedCategory.subcategories].sort((a, b) => a.name.localeCompare(b.name))
        : [];
    const filteredSubcategories = sortedSubcategories.filter(sub =>
        !(sub.name?.toLowerCase().includes('tax') || sub.description?.toLowerCase().includes('tax'))
    );

    const isValidSearchInput = () => {
        if (!mobile) { setValidationError("மொபைல் எண் உள்ளிடவும்"); return false; }
        if (!/^\d{10}$/.test(mobile)) { setValidationError("மொபைல் எண் 10 இலக்கங்கள் இருக்க வேண்டும்"); return false; }
        setValidationError(''); return true;
    };

    const handleSearchGuest = async () => {
        setSearchResult(null);
        setSearchError('');
        setValidationError('');
        setSelectedSubcategories([]);
        setCategoryName('');
        setDonationDate(getTodayDate());
        setTransactionMode('cash');
        setExternalTransactionReference('');
        setPendingDonationData(null);
        setShowConfirmationModal(false);
        setSelfSponsoredWarning(false);

        if (!isValidSearchInput()) return;

        setSearching(true);
        try {
            const resp = await queryGuestForDonation({ mobile });
            if (resp?.members?.length) setSearchResult(resp);
            else {
                setSearchResult(null);
                setValidationError('');
                setSelectedSubcategories([]);
                setCategoryName('');
                setDonationDate(getTodayDate());
                setTransactionMode('cash');
                setExternalTransactionReference('');
                setPendingDonationData(null);
                setShowConfirmationModal(false);
                setSelfSponsoredWarning(false);
                setGuestForm({
                    name: '', mobile: '', area: '', village: '', taluk: '', district: '', pin: ''
                });

            }
        } catch { setSearchError("Error fetching guest data."); }
        finally { setSearching(false); }
    };

    // Subcategory handling
    const handleSubcategoryChange = (sub, checked) => {
        setSelectedSubcategories(prev => {
            if (checked) {
                const matchedDonations = searchResult?.donations?.filter(d =>
                    d.category === selectedCategory?.description &&
                    d.subcategory === sub.description
                ) || [];
                //const totalDonated = matchedDonations.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
                let initialAmount = '';
                if (sub.amount) {
                    const totalDonated = matchedDonations.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
                    const remaining = Math.max(sub.amount - totalDonated, 0);
                    initialAmount = remaining.toFixed(0);
                }

                return [...prev, { subcategory: sub.name, amount: initialAmount, isSelfSponsored: false, noReceipt: false, uniqueOption: sub.uniqueOption }];

            }
            return prev.filter(s => s.subcategory !== sub.name);
        });
    };

    const handleAmountChange = (name, value) => {
        setSelectedSubcategories(prev => prev.map(s =>
            s.subcategory === name ? { ...s, amount: value } : s
        ));
    };

    const handleReceiptChange = (name, receiptNo) => {
        setSelectedSubcategories(prev => prev.map(s => s.subcategory === name ? { ...s, receiptNo } : s));
    };
    const toggleSelfSponsored = (name, checked) => setSelectedSubcategories(prev => prev.map(s => s.subcategory === name ? { ...s, isSelfSponsored: checked } : s));
    const toggleNoReceipt = (name, checked) => setSelectedSubcategories(prev => prev.map(s => s.subcategory === name ? { ...s, noReceipt: checked, receiptNo: checked ? '' : s.receiptNo } : s));

    const handleAddDonation = async () => {
        if (!guestForm.name || !guestForm.mobile) return alert('Please fill guest details');
        if (!categoryName || selectedSubcategories.length === 0) return alert('Please select category & subcategory');

        for (const sub of selectedSubcategories) {
            if (!sub.amount || isNaN(sub.amount) || (!sub.noReceipt && !sub.receiptNo)) {
                return alert("தயவுசெய்து அனைத்து துணை வகைகளுக்கும் தொகை & ரசீது சரிபார்க்கவும்");
            }
        }

        const needsSelfSponsoredPrompt = selectedSubcategories.some(sub => {
            const fullSub = selectedCategory.subcategories.find(s => s.name === sub.subcategory);
            return fullSub?.allowSelfSponsored && !sub.isSelfSponsored;
        });

        setSelfSponsoredWarning(needsSelfSponsoredPrompt);
        setPendingDonationData({
            ...guestForm,
            category: categoryName,
            categoryDescription: selectedCategory?.description || '',
            date: donationDate,
            externalTransactionReference,
            transactionMode,
            subcategories: selectedSubcategories.map(sub => {
                const fullSub = selectedCategory.subcategories.find(s => s.name === sub.subcategory);
                return {
                    subcategory: sub.subcategory,
                    subCategoryDescription: fullSub?.description || '',
                    amount: Number(sub.amount),
                    receiptNo: sub.noReceipt ? '' : sub.receiptNo,
                    isSelfSponsored: !!sub.isSelfSponsored,
                    transactionId: generateTransactionId({ mobile: guestForm.mobile, category: categoryName, subcategory: sub.subcategory, uniqueOption: sub.uniqueOption, amount: sub.amount, date: donationDate, }),
                };
            })
        });
        setShowConfirmationModal(true);
    };

    return (
        <>
            <Navbar />
            <Helmet><title>Guest Donation Create</title></Helmet>

            <div className="general_parent">
                <div className="general_container">
                    <div className="general_body">
                        <Breadcrumbs /><br />
                        <h2>விருந்தினர் நன்கொடை உருவாக்கு</h2>

                        {/* Search */}
                        <br />
                        <div className="auth_element">
                            <label className="auth_label">மொபைல்:</label>
                            <input value={mobile} onChange={e => setMobile(e.target.value)} className="auth_input" maxLength={10} />
                            {searching ? <Loading /> : <button onClick={handleSearchGuest}>தேடு</button>}
                            {validationError && <p style={{ color: 'red' }}>{validationError}</p>}
                        </div>
                        {searchError && <p style={{ color: 'red' }}>{searchError}</p>}

                        {/* Guest form */}
                        <div className="details-card">

                            {['name', 'mobile', 'area', 'village', 'taluk', 'district', 'pin'].map(field => (
                                <div className="auth_element" key={field}>
                                    <label className="auth_label">
                                        {fieldTranslations[field]}:
                                    </label>
                                    <input
                                        className="auth_input"
                                        type="text"
                                        value={guestForm[field]}
                                        onChange={e => handleGuestInputChange(field, e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>
                        {searchResult?.members?.length > 0 && (
                            <div style={{ marginBottom: '1rem' }}>
                                <button onClick={() => navigate("/donation/view/guest?guestId=" + mobile)}>
                                    முந்தைய நன்கொடைகள்
                                </button>
                            </div>
                        )}

                        <br />


                        {/* Donation details */}
                        {categoriesLoading ? <Loading /> :
                            categoriesError ? <p style={{ color: 'red' }}>{categoriesError}</p> :
                                <div className="details-card">
                                    <div className="detail-item">
                                        <label>நன்கொடை வகை:</label>
                                        <select value={categoryName} onChange={e => { setCategoryName(e.target.value); setSelectedSubcategories([]); }} className="auth_select">
                                            <option value="">-- தேர்ந்தெடுக்கவும் --</option>
                                            {categories.filter(cat => !cat.categoryName.includes('Tax')).map(cat => (
                                                <option key={cat.categoryName} value={cat.categoryName}>{cat.description}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Subcategories */}
                                    {filteredSubcategories.map(sub => {
                                        const selected = selectedSubcategories.find(s => s.subcategory === sub.name) || {};
                                        const matchedDonations = searchResult?.donations?.filter(d =>
                                            d.category === selectedCategory?.description &&
                                            d.subcategory === sub.description
                                        ) || [];
                                        const totalDonated = matchedDonations.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
                                        let remaining = null;
                                        let disabled = false;

                                        if (sub.amount) {
                                            const totalDonated = matchedDonations.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
                                            remaining = Math.max(sub.amount - totalDonated, 0);
                                            disabled = remaining <= 0;
                                        }


                                        return (
                                            <div key={sub.name} className="details-card" style={{ marginBottom: '0.5rem' }}>
                                                <label className="auth_checkbox">
                                                    <input type="checkbox" disabled={disabled} checked={!!selected.subcategory} onChange={(e) => handleSubcategoryChange(sub, e.target.checked)} />
                                                    {sub.description} {disabled && <span style={{ color: 'red' }}> (முழுமையாக செலுத்தப்பட்டது)</span>}
                                                </label>

                                                {/* Show past donations */}
                                                {matchedDonations.length > 0 && (
                                                    <div style={{ marginLeft: '1rem', color: 'green' }}>
                                                        <p>முன்னர் செலுத்தியது:</p>
                                                        <ul>
                                                            {matchedDonations.map((d, idx) => (
                                                                <li key={idx}>
                                                                    {new Date(d.transactionDate?._seconds * 1000).toLocaleDateString('ta-IN')} – {formatINR(d.amount)}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                        <p>மொத்தம்: {formatINR(totalDonated)}</p>
                                                    </div>
                                                )}

                                                {/* Entry fields */}
                                                {selected.subcategory && (
                                                    <div style={{ marginLeft: '1rem' }}>
                                                        <div className="auth_element">
                                                            <label className="auth_label">தொகை:</label>
                                                            <input className="auth_input"
                                                                type="text"
                                                                inputMode="numeric"
                                                                pattern="^(0|[1-9]\d*)(\.\d{1,2})?$"
                                                                value={selected.amount}
                                                                onChange={(e) => handleAmountChange(sub.name, e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="auth_element">
                                                            <label className="auth_label">ரசீது எண்:</label>
                                                            <input className="auth_input"
                                                                type="text"
                                                                inputMode="numeric"
                                                                pattern="[0-9]*"
                                                                value={selected.receiptNo}
                                                                onChange={(e) => handleReceiptChange(sub.name, e.target.value)}
                                                            />
                                                        </div>
                                                        <label className="auth_checkbox">
                                                            <input type="checkbox" checked={!!selected.noReceipt} onChange={(e) => toggleNoReceipt(sub.name, e.target.checked)} />
                                                            ரசீது எண் இல்லை
                                                        </label>
                                                        {sub.allowSelfSponsored && (
                                                            <label className="auth_checkbox">
                                                                <input type="checkbox" checked={!!selected.isSelfSponsored} onChange={(e) => toggleSelfSponsored(sub.name, e.target.checked)} />
                                                                நன்கொடையாக செலுத்தப்பட்டது
                                                            </label>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {/* Totals */}
                                    {selectedSubcategories.length > 0 && <div><strong style={{ color: 'red' }}>மொத்த நன்கொடை: ₹{selectedSubcategories.reduce((s, sub) => s + (parseFloat(sub.amount) || 0), 0)}</strong></div>}

                                    {/* Date + Mode */}
                                    <div className="auth_element">
                                        <label className="auth_label">நன்கொடை தேதி:</label>
                                        <input className="auth_input" type="date" value={donationDate || getTodayDate()} onChange={e => setDonationDate(e.target.value)} />
                                    </div>
                                    <div className="auth_element">
                                        <label className="auth_label">பரிவர்த்தனை முறை:</label>
                                        <label><input className="auth_radio" type="radio" value="cash" checked={transactionMode === "cash"} onChange={e => setTransactionMode(e.target.value)} /> Cash</label>
                                        <label><input className="auth_radio" type="radio" value="UPI" checked={transactionMode === "UPI"} onChange={e => setTransactionMode(e.target.value)} /> UPI</label>
                                    </div>
                                    <div className="auth_element">
                                        <label className="auth_label">பரிவர்த்தனை குறிப்பு:</label>
                                        <input className="auth_input" type="text" value={externalTransactionReference} onChange={e => setExternalTransactionReference(e.target.value)} placeholder="GPay எண்" />
                                    </div>

                                    {/* Submit */}
                                    <div>{loading ? <Loading /> : <button onClick={handleAddDonation}>நன்கொடை சேர்க்கவும்</button>}</div>
                                </div>
                        }

                        {/* Confirmation Modal */}
                        {showConfirmationModal && <div className="modal-overlay">
                            <div className="modal-content">
                                <h3>நீங்கள் உறுதியாக நன்கொடை சேர்க்க விரும்புகிறீர்களா?</h3>
                                {selfSponsoredWarning && <p style={{ color: 'red' }}>⚠️ சில துணை வகைகள் தனியுரிமையாக செலுத்த அனுமதிக்கின்றன, ஆனால் நீங்கள் தேர்வு செய்யவில்லை.</p>}
                                <div style={{ marginTop: '1rem' }}>
                                    <button onClick={async () => {
                                        setShowConfirmationModal(false); setLoading(true);
                                        try {
                                            const resp = await createGuestDonation(pendingDonationData);
                                            if (resp) { alert("✅ Donation added successfully!"); navigate("/donation/view/guest?guestId=" + mobile); }
                                        } catch { alert("⚠️ Error adding donation"); }
                                        finally { setLoading(false); setPendingDonationData(null); }
                                    }} style={{ marginRight: '1rem' }}>ஆம், தொடரவும்</button>
                                    <button onClick={() => setShowConfirmationModal(false)}>இல்லை, திரும்பவும்</button>
                                </div>
                            </div>
                        </div>}
                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default GuestDonationCreate;
